import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
// A tiny synchronous localStorage read. Imported eagerly on purpose: the
// router guard renders and cannot await a lazy chunk to decide a redirect.
import { hasSeenWelcome, needsWelcome } from './pages/Welcome';
import { PlayerProvider } from './contexts/PlayerContext';
import { TierProvider } from './contexts/useTier';
import { OfflineProvider } from './contexts/OfflineContext';
import { useSessionRefresh } from './useSessionRefresh';
import { useActivityPing } from './useActivityPing';
import AppLayout from './components/layout/AppLayout';
// Mounted once, listens for a window event, shows the confirmation sheet after
// a payment. See PurchaseReceipt.js for why it is an event and not a context.
import PurchaseReceipt from './components/PurchaseReceipt';
import MerchParked from './components/MerchParked';
import { MERCH_PARKED } from './config/features';
// The offline library. A static import, not React.lazy, on purpose: it is the
// one page that has to render with no network, and a lazy chunk that was never
// fetched cannot be loaded offline, the person would tap Offline and sit on
// the Suspense fallback forever. A few KB in the main bundle is the difference
// between the feature working on a plane and not.
import OfflinePage from './pages/OfflinePage';
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const ComparisonPage = React.lazy(() => import('./pages/ComparisonPage'));
const HomePage = React.lazy(() => import('./pages/HomePage'));
const Welcome = React.lazy(() => import('./pages/Welcome'));
const BrowsePage = React.lazy(() => import('./pages/BrowsePage'));
// The two hub pages. Added because Google had discovered 233 of this site's
// URLs and crawled none of them: nothing linked to them. See
// netlify/lib/seoCatalogue.js for the whole diagnosis.
const ArtistsIndexPage = React.lazy(() => import('./pages/ArtistsIndexPage'));
const GenrePage = React.lazy(() => import('./pages/GenrePage'));
// Answer pages. The comparison pages at /vs/* already earn their keep in
// principle and had never been crawled; these are the same idea pointed at
// the questions a South African artist actually searches for.
const GuidesIndexPage = React.lazy(() => import('./pages/GuidesIndexPage'));
const GuidePage = React.lazy(() => import('./pages/GuidePage'));
const FaqPage = React.lazy(() => import('./pages/FaqPage'));
const SplitsPage = React.lazy(() => import('./pages/SplitsPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const LibraryPage = React.lazy(() => import('./pages/LibraryPage'));
const LikedSongsPage = React.lazy(() => import('./pages/LikedSongsPage'));
const DownloadsPage = React.lazy(() => import('./pages/DownloadsPage'));
const FollowingPage = React.lazy(() => import('./pages/FollowingPage'));
const PlaylistsPage = React.lazy(() => import('./pages/PlaylistsPage'));
const PlaylistDetailPage = React.lazy(() => import('./pages/PlaylistDetailPage'));
const PlaylistJoinPage = React.lazy(() => import('./pages/PlaylistJoinPage'));
const ListeningSessionPage = React.lazy(() => import('./pages/ListeningSessionPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const FeedPage = React.lazy(() => import('./pages/FeedPage'));
const ArtistDashboard = React.lazy(() => import('./pages/ArtistDashboard'));
const ArtistProfilePage = React.lazy(() => import('./pages/ArtistProfilePage'));
const TierUpgradePage = React.lazy(() => import('./pages/TierUpgradePage'));
const ChatRoomsPage = React.lazy(() => import('./pages/ChatRoomsPage'));
const ChatRoomView = React.lazy(() => import('./pages/ChatRoomView'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = React.lazy(() => import('./pages/TermsOfUse'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
const HubPage = React.lazy(() => import('./pages/HubPage'));
const ProfileSetup = React.lazy(() => import('./pages/ProfileSetup'));
const ListenerStatsPage = React.lazy(() => import('./pages/ListenerStatsPage'));
const ListenerUpgradePage = React.lazy(() => import('./pages/ListenerUpgradePage'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AdminArtists = React.lazy(() => import('./pages/AdminArtists'));
const AdminAnalytics = React.lazy(() => import('./pages/AdminAnalytics'));
const AdminModeration = React.lazy(() => import('./pages/AdminModeration'));
const AdminBoost = React.lazy(() => import('./pages/AdminBoost'));
const AdminBroadcast = React.lazy(() => import('./pages/AdminBroadcast'));
const RecentlyPlayedPage = React.lazy(() => import('./pages/RecentlyPlayedPage'));
const UserProfilePage = React.lazy(() => import('./pages/UserProfilePage'));
const AlbumDetailPage = React.lazy(() => import('./pages/AlbumDetailPage'));
const TrackDetailPage = React.lazy(() => import('./pages/TrackDetailPage'));
const AffiliatePage = React.lazy(() => import('./pages/AffiliatePage'));
const AdminAffiliates = React.lazy(() => import('./pages/AdminAffiliates'));
const AdminPeople = React.lazy(() => import('./pages/AdminPeople'));
const AdminIntelligence = React.lazy(() => import('./pages/AdminIntelligence'));
const AdminContent = React.lazy(() => import('./pages/AdminContent'));
const AdminGrowth = React.lazy(() => import('./pages/AdminGrowth'));
const BeatDetailPage = React.lazy(() => import('./pages/BeatDetailPage'));
const AdminUserBehaviorPage = React.lazy(() => import('./pages/AdminUserBehaviorPage'));
const TrackPage = React.lazy(() => import('./pages/TrackPage'));
const CollabRadarPage = React.lazy(() => import('./pages/CollabRadarPage'));
const AdminDuplicates = React.lazy(() => import('./pages/AdminDuplicates'));
const GuardianConsentPage = React.lazy(() => import('./pages/GuardianConsentPage'));
const ShortLinkPage = React.lazy(() => import('./pages/ShortLinkPage'));
const AdminGuardianConsents = React.lazy(() => import('./pages/AdminGuardianConsents'));
const AdminPersonas = React.lazy(() => import('./pages/AdminPersonas'));
const CompetitionRoomPage = React.lazy(() => import('./pages/CompetitionRoomPage'));
const WheelRevealPage = React.lazy(() => import('./pages/WheelRevealPage'));
const ForYouPage = React.lazy(() => import('./pages/ForYouPage'));
const MerchPage = React.lazy(() => import('./pages/MerchPage'));
const MerchCheckoutPage = React.lazy(() => import('./pages/MerchCheckoutPage'));
const MerchOrdersPage = React.lazy(() => import('./pages/MerchOrdersPage'));
const CompetitionsPage = React.lazy(() => import('./pages/CompetitionsPage'));
const AdminCompetitions = React.lazy(() => import('./pages/AdminCompetitions'));
const AdminEngagement = React.lazy(() => import('./pages/AdminEngagement'));
const FanLeaderboardPage = React.lazy(() => import('./pages/FanLeaderboardPage'));
const RecentlyDiscoveredPage = React.lazy(() => import('./pages/RecentlyDiscoveredPage'));
const ListenerProfilePage = React.lazy(() => import('./pages/ListenerProfilePage'));
const SchoolSessionsPage = React.lazy(() => import('./pages/SchoolSessionsPage'));
const SchoolSessionsVotePage = React.lazy(() => import('./pages/SchoolSessionsVotePage'));
const SchoolSessionsTermsPage = React.lazy(() => import('./pages/SchoolSessionsTermsPage'));
const SchoolSessionsJudgePanel = React.lazy(() => import('./pages/SchoolSessionsJudgePanel'));
const AdminSchoolSessions = React.lazy(() => import('./pages/AdminSchoolSessions'));
const AdminRetail = React.lazy(() => import('./pages/AdminRetail'));
const AdminDeals = React.lazy(() => import('./pages/AdminDeals'));
const CreatorDealPage = React.lazy(() => import('./pages/CreatorDealPage'));
const RetailPlayerPage = React.lazy(() => import('./pages/RetailPlayerPage'));
const NewsletterComposePage = React.lazy(() => import('./pages/NewsletterComposePage'));
const NewsletterPostPage = React.lazy(() => import('./pages/NewsletterPostPage'));
const LegalDocumentPage = React.lazy(() => import('./pages/LegalDocumentPage'));
const VipCardPrintPage = React.lazy(() => import('./pages/VipCardPrintPage'));
const DrivingPage = React.lazy(() => import('./pages/DrivingPage'));
const RetailJoinPage = React.lazy(() => import('./pages/RetailJoinPage'));
const RetailLandingPage = React.lazy(() => import('./pages/RetailLandingPage'));
const RetailStartPage = React.lazy(() => import('./pages/RetailStartPage'));
const AdminHomeHero = React.lazy(() => import('./pages/AdminHomeHero'));
const AdminNews = React.lazy(() => import('./pages/AdminNews'));
const AdminColdStart = React.lazy(() => import('./pages/AdminColdStart'));
const AdminFeed = React.lazy(() => import('./pages/AdminFeed'));
const LearnPage = React.lazy(() => import('./pages/LearnPage'));
const HiddenPage = React.lazy(() => import('./pages/HiddenPage'));
const ContactPreferencesPage = React.lazy(() => import('./pages/ContactPreferencesPage'));
const ArtistCollectionPage = React.lazy(() => import('./pages/ArtistCollectionPage'));
// AdminRetailStaff.js is superseded by the Staff tab in RetailAdminPanel.
// Retail management belongs inside Retail, and keeping two implementations
// is how ProfilePage and ProfileSetup drifted apart. The old path redirects
// so existing links and bookmarks still work. The page file can be deleted.
const RetailTermsPage = React.lazy(() => import('./pages/RetailTermsPage'));
const RetailPrivacyPage = React.lazy(() => import('./pages/RetailPrivacyPage'));
const RetailAdminPage = React.lazy(() => import('./pages/RetailAdminPage'));

// ── Session keepalive, refreshes token + listens for activity ───────────────
function SessionManager() {
  useSessionRefresh();
  useActivityPing();
  return null;
}

// ── Wrapper to set page title for standalone pages outside AppLayout ─────────
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function PageTitle({ title, children }) {
  return (
    <>
      <Helmet>
        <title>{title} · Feelz Machine</title>
      </Helmet>
      {children}
    </>
  );
}

// Wildcard fallback for anything no other route matches. Also handles
// /@username short URLs here specifically, React Router's pattern syntax
// can't express "@" immediately followed by a splat with no separating
// slash (confirmed: /@:slug never matches because the colon isn't right
// after a slash, and /@* isn't allowed because * must always follow a
// slash). Checking the raw pathname here, in the one place guaranteed to
// run last, sidesteps the limitation entirely instead of fighting it.
function NotFoundRedirect() {
  const location = useLocation();
  // Vanity URLs under a handle:
  //   /@stevecsa                      -> /artist/stevecsa
  //   /@stevecsa/single/<trackSlug>   -> /track/<trackSlug>
  //   /@stevecsa/album/<albumSlug>    -> /album/stevecsa/<albumSlug>
  //
  // Redirects rather than routes that render in place, deliberately. Two
  // URLs serving the same page splits its search ranking and gives the
  // crawler two things to index, so the shareable handle form points at the
  // one canonical page instead of competing with it.
  const vanityTrack = location.pathname.match(/^\/@([^/]+)\/(?:single|track)\/(.+)$/);
  if (vanityTrack) {
    return <Navigate to={`/track/${vanityTrack[2]}`} replace />;
  }
  // This one was the worst of the three, because it is the SHARE link.
  // /@handle/album/<slug> redirected to /album/<handle>/<slug>, two segments
  // against a one-segment route, so it came straight back to this same
  // catch-all and then fell through to "/". Every album link anyone has ever
  // shared has landed on For You. The handle is not part of the destination;
  // AlbumDetailPage looks the album up by slug.
  const vanityAlbum = location.pathname.match(/^\/@([^/]+)\/album\/(.+)$/);
  if (vanityAlbum) {
    return <Navigate to={`/album/${vanityAlbum[2]}`} replace />;
  }
  const vanityBeat = location.pathname.match(/^\/@([^/]+)\/beat\/(.+)$/);
  if (vanityBeat) {
    return <Navigate to={`/beat/${vanityBeat[2]}`} replace />;
  }

  const atMatch = location.pathname.match(/^\/@([^/]+)$/);
  if (atMatch) {
    return <Navigate to={`/artist/${atMatch[1]}`} replace />;
  }
  return <Navigate to="/" replace />;
}

// Handles Printful OAuth redirect, passes code back to artist profile
function MerchConnectCallback() {
  const navigate = useNavigate();
  React.useEffect(() => {
    const params  = new URLSearchParams(window.location.search);
    const code    = params.get('code');
    const state   = params.get('state'); // artist slug stored in state param
    if (code && state) {
      navigate(`/artist/${state}?printful_code=${code}`, { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  }, []); // eslint-disable-line
  return null;
}

// Captures ?ref= param from URL and logs affiliate click
function AffiliateTracker() {
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (!ref) return;
    // Store ref for conversion tracking on signup
    try { sessionStorage.setItem('feelz_ref', ref); } catch {}
    // Log click
    fetch('/.netlify/functions/affiliate-track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'click', refCode: ref, page: window.location.pathname }),
    }).catch(() => {});
  }, []);
  return null;
}


// Redirects new users to /setup if they have no artist or listener profile
function OnboardingGuard({ children }) {
  const { user, artist, listener, loading } = useAuth();
  const location = useLocation();

  // THE DECISION MOVED HERE, FROM INSIDE THE FEED.
  //
  // The row test below cannot catch a new sign-up, and that is not a bug in
  // the test, it is arithmetic: AuthContext creates a listeners row on first
  // login, so by the time this runs `listener` is already truthy and the
  // condition is false for every new account. Nothing here ever redirected
  // them.
  //
  // What actually sent people to /welcome was an effect inside ForYouPage,
  // which only runs once that page has mounted. So a new account watched the
  // feed appear, got a moment of it, and was then yanked away. That is what
  // "it just goes to For You" was: onboarding arriving late and from the wrong
  // place, not onboarding missing.
  //
  // needsWelcome asks the durable marker in the database, so it is async and a
  // guard cannot await. It runs once per account here and the app holds while
  // it answers, which costs one round trip and ONLY for somebody who has no
  // local completion flag. Everybody else takes the synchronous path below and
  // waits for nothing.
  const [needsFlow, setNeedsFlow] = React.useState(null);
  React.useEffect(() => {
    if (!user) { setNeedsFlow(false); return; }
    if (hasSeenWelcome(user.id)) { setNeedsFlow(false); return; }
    let dead = false;
    // A hard floor on the wait. If this read is slow or never answers, the
    // app opens rather than holding on a blank screen: being let in without
    // onboarding is a far better failure than an account that cannot load.
    const bail = setTimeout(() => { if (!dead) setNeedsFlow(false); }, 2500);
    needsWelcome(user, artist)
      .then(n => { if (!dead) { clearTimeout(bail); setNeedsFlow(!!n); } })
      .catch(() => { if (!dead) { clearTimeout(bail); setNeedsFlow(false); } });
    return () => { dead = true; clearTimeout(bail); };
  }, [user, artist]);
  const skipPaths = ['/setup', '/welcome', '/login', '/reset-password', '/about', '/terms-of-use', '/privacy-policy', '/artist/', '/@', '/schoolsessions', '/sessions'];

  // Public paths always render immediately, regardless of auth loading state.
  // This matters specifically for the /@slug -> /artist/slug redirect: the URL
  // changes before auth finishes resolving, and without this check the
  // /artist/ skip only applied once loading was false, leaving a blank
  // render in between that looked like the page just wasn't going anywhere.
  if (skipPaths.some(p => location.pathname.startsWith(p))) return children;

  if (loading) return null;
  if (!user) return children;

  // New user, has auth but no profile.
  //
  // This used to send everybody to /setup, which is a settings page. An artist
  // can make sense of that. A listener lands on a wall of fields with no idea
  // why they are looking at it, having asked to listen to music.
  //
  // So it now depends on what they said they were here to do at sign-up.
  // LoginPage stores that choice, and AuthContext clears it the moment it uses
  // it, so reading it here is safe: if it is still set, the artist row has not
  // been made yet and /setup is where they belong.
  //
  // hasSeenWelcome is the circuit breaker, and it is not decoration.
  //
  // This condition reads the TABLES. If the rows are missing for any reason,
  // and finishing the flow fails to create them, the person is sent back here
  // on every single sign in with no way out. That happened: Welcome.js used
  // `.update()` on listeners, which matches nothing when there is no row and
  // reports success anyway, so an account with no listeners row was asked what
  // it wanted to be forever.
  //
  // The write is fixed. This is the second lock on the same door: once
  // somebody has been through the flow, they are let into the app even if the
  // rows are not what we expect. A thin profile they can fix in settings is a
  // far better failure than an account that cannot be used at all.
  // The local flag is checked FIRST, synchronously, on every render.
  //
  // Without this the guard loops. needsFlow is state, computed once when the
  // account loads, and finishing onboarding changes neither `user` nor
  // `artist`, so the effect never re-runs and the stale `true` sends the
  // person straight back to /welcome the moment they leave it. Which is
  // exactly what happened: complete the flow, land back on the first step,
  // forever.
  //
  // finish() writes this flag before it navigates, so reading it here is what
  // lets somebody out. State answers "do they need it", this answers "are they
  // already done", and the second question has to be asked first.
  if (user && hasSeenWelcome(user.id)) return children;

  // Still unknown, and only ever unknown for an account with no local flag.
  if (needsFlow === null) return null;
  if (needsFlow) return <Navigate to="/welcome" replace />;

  // THE BRANCH THAT USED TO BE HERE OVERRODE THE ANSWER ABOVE IT.
  //
  // It read `!artist && !listener && !hasSeenWelcome(...)` and redirected to
  // /welcome. Every one of those three is true for somebody signing in on a
  // SECOND device: localStorage is empty there by definition, and `artist` and
  // `listener` are both still null for the first renders while AuthContext
  // reads them.
  //
  // So the sequence was: needsWelcome asks the database, finds
  // welcome_completed_at set, correctly answers "no", sets needsFlow to false,
  // and then execution fell straight through to this branch and sent the
  // person to onboarding anyway. The durable marker was read, believed, and
  // then ignored two lines later.
  //
  // Reproduced exactly: an account with welcome_completed_at set at 18:33 and
  // a complete profile, name, genre, mood, bio and all, asked to onboard again
  // on a PC.
  //
  // Reaching this line means needsFlow is false, which means either the
  // durable marker said done or the 2.5s floor expired. Both of those are
  // already a decision to let the person in. There is nothing left to infer,
  // and inference is what the comment above has been warning about for three
  // paragraphs.
  return children;
}


function PaymentSuccess() {
  const navigate = useNavigate();
  React.useEffect(() => {
    // Send the buyer back where they came from; the beat page picks up the
    // completed purchase on mount and starts the download.
    const t = setTimeout(() => {
      // Try to go back; if no history, go to hub
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/hub');
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [navigate]);
  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center space-y-4 px-6 text-center">
      <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
        <span className="text-3xl">✓</span>
      </div>
      <h2 className="text-xl font-bold text-white">Payment Successful!</h2>
      <p className="text-sm text-white/40">Your download will start shortly. Redirecting you back…</p>
    </div>
  );
}

function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center space-y-4 px-6 text-center">
      <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
        <span className="text-3xl">✕</span>
      </div>
      <h2 className="text-xl font-bold text-white">Payment Cancelled</h2>
      <p className="text-sm text-white/40">No charge was made.</p>
      <button onClick={() => navigate(-1)} className="px-6 py-2.5 bg-white text-black rounded-xl text-sm font-semibold">Go Back</button>
    </div>
  );
}


export default function AppRouter() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <SessionManager />
        <AffiliateTracker />
        <AuthProvider>
          <PlayerProvider>
            <TierProvider>
            <OfflineProvider>
            <OnboardingGuard>
            <PurchaseReceipt />
            <Suspense fallback={<div style={{ minHeight: '100vh', background: '#000' }} />}>
            <Routes>
              {/* Legacy /player/* redirects */}
              <Route path="/player" element={<Navigate to="/" replace />} />
              <Route path="/player/*" element={<Navigate to="/" replace />} />

              {/* Fix: /terms was broken, redirect to correct route */}
              <Route path="/terms" element={<Navigate to="/terms-of-use" replace />} />

              <Route path="/login" element={<LoginPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/chat/:roomId" element={<ChatRoomView />} />
              <Route path="/competition/:competitionId" element={<CompetitionRoomPage />} />
              <Route path="/session/:sessionId" element={<ListeningSessionPage />} />
              <Route path="/merch-connect-callback" element={<MerchConnectCallback />} />

              {/* Legal pages, fixed titles */}
              <Route path="/privacy-policy" element={
                <PageTitle title="Privacy Policy">
                  <PrivacyPolicy />
                </PageTitle>
              } />
              <Route path="/terms-of-use" element={
                <PageTitle title="Terms of Use">
                  <TermsOfUse />
                </PageTitle>
              } />
              <Route path="/admin/vip-card-print/:candidateId" element={<VipCardPrintPage />} />

              {/* Driving mode renders its own full-screen shell and must stay
                  OUTSIDE AppLayout. Inside it, the sidebar, the mini player
                  and the mini player's toast all still mount, and the toast
                  sits at z-[400] against this page's z-[100], so a notice
                  would land on top of the controls. Out here nothing else
                  renders at all, which is the whole idea of the screen.
                  PlayerProvider wraps the entire route table, so the audio
                  element and the queue carry straight through. */}
              <Route path="/driving" element={<DrivingPage />} />
              {/* Retail renders its own full-screen shell and is a separate
                  product, so it must stay outside AppLayout or it gets the
                  Feelz Machine sidebar on top of its own chrome. Same reason
                  VipCardPrintPage, RetailJoinPage, RetailTermsPage and
                  RetailPrivacyPage were moved out previously. */}
              <Route path="/retail" element={<RetailLandingPage />} />
              <Route path="/retail/start" element={<RetailStartPage />} />
              <Route path="/retail/player" element={<RetailPlayerPage />} />
              <Route path="/retail-admin" element={<RetailAdminPage />} />
              <Route path="/retail/join/:token" element={<RetailJoinPage />} />
              <Route path="/retail/terms" element={<RetailTermsPage />} />
              <Route path="/retail/privacy" element={<RetailPrivacyPage />} />
              <Route element={<AppLayout />}>
                <Route path="/" element={<ForYouPage />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/welcome" element={<Welcome />} />

                {/* Sessions lives INSIDE the app shell.
                    These three used to render outside AppLayout, next to the
                    Retail pages. Retail belongs out there: it is a separate
                    product with its own chrome. Sessions is not. It is
                    a competition on Feelz Machine, so opening it dropped you
                    onto a page with no nav, no player and no way back into the
                    app short of the browser's back button, which does nothing
                    at all when the page was reached from a shared link.
                    The judge panel was already inside; these three now match
                    it. Signed-out visitors arriving from TikTok get the shell
                    too, which is the point: the nav is how they find the rest
                    of the platform. */}
                {/* Short share links, migration 185. Both redirect to the
                    canonical page; see the note in ShortLinkPage.js. */}
                <Route path="/t/:code" element={<ShortLinkPage />} />
                <Route path="/a/:code" element={<ShortLinkPage />} />

                {/* Sessions, at /sessions from now on.
                    /schoolsessions is kept as a living alias rather than a
                    redirect, and permanently: VipCardPrintPage prints
                    "feelzmachine.com/schoolsessions" onto a physical card, so
                    that path has to keep resolving for every card already in
                    somebody's hand. Same components, two paths, nothing to
                    break. New links should use /sessions. */}
                <Route path="/sessions" element={
                  <PageTitle title="Sessions"><SchoolSessionsPage /></PageTitle>
                } />
                <Route path="/sessions/vote" element={
                  <PageTitle title="Vote: Sessions"><SchoolSessionsVotePage /></PageTitle>
                } />
                <Route path="/sessions/terms" element={
                  <PageTitle title="Sessions: Terms"><SchoolSessionsTermsPage /></PageTitle>
                } />
                <Route path="/sessions/judge" element={<SchoolSessionsJudgePanel />} />

                <Route path="/schoolsessions" element={
                  <PageTitle title="Sessions"><SchoolSessionsPage /></PageTitle>
                } />
                <Route path="/schoolsessions/vote" element={
                  <PageTitle title="Vote: Sessions"><SchoolSessionsVotePage /></PageTitle>
                } />
                <Route path="/schoolsessions/terms" element={
                  <PageTitle title="Sessions: Terms"><SchoolSessionsTermsPage /></PageTitle>
                } />
                <Route path="/schoolsessions/judge" element={<SchoolSessionsJudgePanel />} />
                <Route path="/for-you" element={<Navigate to="/" replace />} />
                <Route path="/browse" element={<BrowsePage />} />
                <Route path="/artists" element={<ArtistsIndexPage />} />
                <Route path="/genres/:slug" element={<GenrePage />} />
                <Route path="/wheel" element={<WheelRevealPage />} />
                <Route path="/competitions" element={<CompetitionsPage />} />
                <Route path="/library" element={<LibraryPage />} />
                <Route path="/library/likes" element={<LikedSongsPage />} />
                <Route path="/library/downloads" element={<DownloadsPage />} />
                <Route path="/library/offline" element={<OfflinePage />} />
                <Route path="/library/recent" element={<RecentlyPlayedPage />} />
                <Route path="/library/following" element={<FollowingPage />} />
                <Route path="/listener/stats"   element={<ListenerStatsPage />} />
                <Route path="/upgrade" element={<TierUpgradePage />} />
                <Route path="/listener/upgrade" element={<ListenerUpgradePage />} />
                <Route path="/library/playlists" element={<PlaylistsPage />} />
                <Route path="/library/playlists/join/:token" element={<PlaylistJoinPage />} />
                <Route path="/library/playlists/:id" element={<PlaylistDetailPage />} />
                <Route path="/community" element={<ChatRoomsPage />} />
                <Route path="/feed" element={<FeedPage />} />
                <Route path="/chat" element={<Navigate to="/community" replace />} />
                {/* Inside AppLayout. It used to render outside it, so anyone
                    who signed in and landed on setup had no side nav and no
                    way back into the app. ProfileSetup has no shell of its
                    own, so it needed the route moved, nothing else. */}
                <Route path="/setup" element={<ProfileSetup />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/profile/edit" element={<UserProfilePage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/hub" element={<HubPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/learn" element={<LearnPage />} />
                <Route path="/vs/:platform" element={<ComparisonPage />} />
                <Route path="/guides" element={<GuidesIndexPage />} />
                <Route path="/guides/:slug" element={<GuidePage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/album/:id" element={<AlbumDetailPage />} />
                {/* TrackDetailPage consolidated into TrackPage below */}
                <Route path="/affiliates" element={<AffiliatePage />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/admin/affiliates"   element={<AdminAffiliates />} />
                <Route path="/admin/people"       element={<AdminPeople />} />
                <Route path="/admin/intelligence" element={<AdminIntelligence />} />
                <Route path="/admin/content"      element={<AdminContent />} />
                <Route path="/admin/growth"       element={<AdminGrowth />} />
                <Route path="/beat/:slug" element={<BeatDetailPage />} />
                <Route path="/artist/:slug" element={<ArtistProfilePage />} />
                {/* Merch is parked, src/config/features.js.
                    The switch is HERE, at the route, and not inside the three
                    pages: an early return above a component's hooks changes
                    the number of hooks React sees between renders, which the
                    build refuses outright (rules-of-hooks). At the route there
                    is no component to half-render, the parked notice is
                    simply what the URL resolves to, so a bookmarked shop link
                    gets the explanation instead of a checkout, and the pages
                    themselves stay exactly as they were for when it comes
                    back. */}
                <Route path="/artist/:slug/merch"
                  element={MERCH_PARKED ? <MerchParked /> : <MerchPage />} />
                <Route path="/artist/:slug/merch/checkout"
                  element={MERCH_PARKED ? <MerchParked /> : <MerchCheckoutPage />} />
                <Route path="/artist/:slug/merch/orders"
                  element={MERCH_PARKED ? <MerchParked /> : <MerchOrdersPage />} />
                <Route path="/artist/:slug/fans" element={<FanLeaderboardPage />} />
                {/* The full lists behind "See all" on the profile's Albums and
                    Singles cards. Declared before nothing else that could
                    shadow them, since /artist/:slug/* is a shared prefix. */}
                <Route path="/artist/:slug/albums" element={<ArtistCollectionPage kind="albums" />} />
                <Route path="/artist/:slug/singles" element={<ArtistCollectionPage kind="singles" />} />

                <Route path="/track/:slug" element={<TrackPage />} />
                <Route path="/dashboard" element={<ArtistDashboard />} />
                <Route path="/collab-radar" element={<CollabRadarPage />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/artists" element={<AdminArtists />} />
                <Route path="/admin/analytics" element={<AdminAnalytics />} />
                <Route path="/admin/moderation" element={<AdminModeration />} />
                <Route path="/admin/boost" element={<AdminBoost />} />
                <Route path="/admin/broadcast" element={<AdminBroadcast />} />
                <Route path="/admin/behavior" element={<AdminUserBehaviorPage />} />
                <Route path="/admin/duplicates" element={<AdminDuplicates />} />
                <Route path="/admin/competitions" element={<AdminCompetitions />} />
                <Route path="/admin/school-sessions" element={<AdminSchoolSessions />} />
                <Route path="/admin/retail" element={<AdminRetail />} />
                <Route path="/admin/deals" element={<AdminDeals />} />
                <Route path="/deal/:id" element={<CreatorDealPage />} />
                <Route path="/newsletter/compose" element={<NewsletterComposePage />} />
                <Route path="/newsletter/:slug" element={<NewsletterPostPage />} />
                <Route path="/legal/:slug" element={<LegalDocumentPage />} />
                <Route path="/splits" element={<SplitsPage />} />
                <Route path="/admin/home-hero" element={<AdminHomeHero />} />
                <Route path="/admin/news" element={<AdminNews />} />
                <Route path="/admin/cold-start" element={<AdminColdStart />} />
                <Route path="/admin/feed" element={<AdminFeed />} />
                <Route path="/admin/guardian-consents" element={<AdminGuardianConsents />} />
                <Route path="/admin/personas" element={<AdminPersonas />} />
                <Route path="/guardian-consent" element={<GuardianConsentPage />} />
                <Route path="/hidden" element={<HiddenPage />} />
                <Route path="/contact-preferences" element={<ContactPreferencesPage />} />
                <Route path="/admin/retail-staff" element={<Navigate to="/retail-admin?sub=staff" replace />} />
                <Route path="/admin/engagement" element={<AdminEngagement />} />
                <Route path="/library/discovered" element={<RecentlyDiscoveredPage />} />
                <Route path="/listener/:userId" element={<ListenerProfilePage />} />

                {/* Catch-all: unknown routes redirect home */}
                <Route path="*" element={<NotFoundRedirect />} />
              </Route>
            </Routes>
            </Suspense>
            </OnboardingGuard>
            </OfflineProvider>
            </TierProvider>
          </PlayerProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}